import { useTranslation } from "react-i18next"
import { useCallback, useEffect, useState } from "react"
import { isEqual } from "lodash"

import TextField from "@/components/form/textField/TextField"
import {
  DEFAULT_ROLL_ANGLE_THRESHOLD,
  MINIMUM_ROLL_ANGLE_THRESHOLD,
  MAXIMUM_ROLL_ANGLE_THRESHOLD,
} from "@/app/settings/metric/actions/constants"
import { Dive, ReadDiveActionResponse } from "@/types/setting"
import { createActionSettingState } from "@/stores/settings"
import { ActionSettingType } from "@/constants/unit"

export default function DiveActionTab({
  initialData,
  editedData,
  setEditedData,
}: {
  initialData: ReadDiveActionResponse | undefined
  editedData: ReadDiveActionResponse | null
  setEditedData: (state: ReadDiveActionResponse) => void
}) {
  const { t } = useTranslation()
  const { setIsAllValid, setHasChanges } = createActionSettingState()

  const [diveValue, setDiveValue] = useState<Dive>({
    rollAngleThreshold: DEFAULT_ROLL_ANGLE_THRESHOLD,
  })
  const [lastTouched, setLastTouched] = useState<boolean>(false)

  useEffect(() => {
    const newValue = editedData?.global || initialData?.global
    if (newValue) {
      setDiveValue((prev) => {
        if (isEqual(prev, newValue)) {
          return prev
        }

        return newValue
      })
    }
  }, [initialData, editedData])

  useEffect(() => {
    if (!initialData) {
      return
    }

    const hasChanged = !isEqual(initialData.global, diveValue)
    setHasChanges(ActionSettingType.DIVE, hasChanged)
  }, [initialData, diveValue])

  useEffect(() => {
    setIsAllValid(ActionSettingType.DIVE, !isErr())
  }, [diveValue])

  const isErr = useCallback(() => {
    const isEmpty = !diveValue.rollAngleThreshold
    const isUnder30 = diveValue.rollAngleThreshold < MINIMUM_ROLL_ANGLE_THRESHOLD
    const isOver90 = diveValue.rollAngleThreshold > MAXIMUM_ROLL_ANGLE_THRESHOLD

    return isEmpty || isUnder30 || isOver90
  }, [diveValue])

  const getErrMessage = () => {
    if (diveValue.rollAngleThreshold < MINIMUM_ROLL_ANGLE_THRESHOLD) {
      return t("rules.min", {
        name: `${t("dive")} ${t("threshold")}`,
        min: MINIMUM_ROLL_ANGLE_THRESHOLD,
      })
    } else if (diveValue.rollAngleThreshold > MAXIMUM_ROLL_ANGLE_THRESHOLD) {
      return t("rules.max", {
        name: `${t("dive")} ${t("threshold")}`,
        max: MAXIMUM_ROLL_ANGLE_THRESHOLD,
      })
    } else {
      return ""
    }
  }

  const handleChange = (value: number) => {
    const newDiveValue = {
      ...diveValue,
      rollAngleThreshold: value,
    }
    setDiveValue(newDiveValue)
    setEditedData({ global: newDiveValue })
  }

  const handleBlur = (value: number) => {
    const roundedValue = Math.round(value)
    const newDiveValue = {
      ...diveValue,
      rollAngleThreshold: roundedValue,
    }
    setDiveValue(newDiveValue)
    setEditedData({ global: newDiveValue })
    setLastTouched(false)
  }

  return (
    <div className="ActionRowCont">
      <div className="row-title-editable">
        <p className="row-title-default-label">{`${t(`metric.dive`)} ${t("effort")}`}</p>
        <div className="row-title-label">
          <p>{t("metric.leftRightDiveCount")}</p>
        </div>
      </div>

      <div className="ActionRowItemCont">
        <div className="action-threshold">
          <p className="action-form-label">{t("threshold")} (°)</p>
          <div>
            <TextField
              type="number"
              value={diveValue?.rollAngleThreshold || 0}
              onChange={(e) => handleChange(Number(e.target.value))}
              onBlur={(e) => handleBlur(Number(e.target.value))}
              onFocus={() => setLastTouched(true)}
              error={isErr()}
              showMessage={lastTouched && isErr()}
              message={getErrMessage()}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
